// Phase COLLEGE-PRICING-1 · shared public card render — used by BOTH
// the public page (/college-logistics-pricing) and the CMS live preview.
//
// Props:
//   page: {heading, subheading, highlight, most_popular_enabled, most_popular_badge_text}
//   cards: [{...}] (already sorted by order_index by caller)
//   variant: "desktop" | "mobile"  (preview pane uses this; public route auto)
//
// Single source of truth so preview === live.
import { Luggage, Package, PackageOpen, Layers, Truck, ImageIcon, MessageCircle } from "lucide-react";

const ORANGE = "#FF6B00";

const ICON_MAP = {
  suitcase:    Luggage,
  duffel:      Package,
  box:         PackageOpen,
  boxes:       Layers,
  truck:       Truck,
};

function CardIcon({ icon, icon_url, color }) {
  if (icon === "custom_url" && icon_url) {
    return <img src={icon_url} alt="" className="h-10 w-10 object-contain"
                 data-testid="card-icon-custom"/>;
  }
  const Cmp = ICON_MAP[icon] || ImageIcon;
  return <Cmp className="h-10 w-10" style={{ color }} data-testid={`card-icon-${icon}`}/>;
}

function buildWhatsAppHref(number, prefilledMessage) {
  // wa.me requires no leading + and URL-encoded text.
  return `https://wa.me/${number}?text=${encodeURIComponent(prefilledMessage || "")}`;
}

export function PricingCard({ card, badgeEnabled, badgeText }) {
  const showBadge = badgeEnabled && card.is_most_popular;
  const baseBorder = showBadge ? "2px solid" : "1px solid";
  const borderColor = showBadge ? card.border_color : "#E5E7EB";
  const tintBg = showBadge ? `${card.border_color}1A` : "transparent"; // ~10% alpha
  return (
    <div className="relative bg-white rounded-2xl p-5 shadow-sm flex flex-col h-full"
         style={{
           border: `${baseBorder} ${borderColor}`,
           backgroundColor: showBadge ? tintBg : "white",
         }}
         data-testid={`college-card-${card.card_id}`}
         data-is-most-popular={showBadge ? "true" : "false"}>
      {showBadge && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] font-bold px-3 py-1 rounded-full text-white tracking-wider"
             style={{ backgroundColor: card.border_color }}
             data-testid={`college-card-${card.card_id}-badge`}>
          {badgeText}
        </div>
      )}
      <div className="mb-3"><CardIcon icon={card.icon} icon_url={card.icon_url} color={showBadge ? card.border_color : "#475569"}/></div>
      <h3 className="text-lg font-bold text-slate-900" data-testid={`college-card-${card.card_id}-title`}>{card.title}</h3>
      {card.subtitle && <p className="text-sm text-slate-600 mt-0.5">{card.subtitle}</p>}
      <div className="mt-3 mb-2" data-testid={`college-card-${card.card_id}-price`}>
        <span className="text-sm font-medium text-slate-500">{card.price_prefix}</span>
        <span className="text-4xl font-extrabold text-slate-900 ml-0.5">{card.price_value}</span>
        <span className="text-sm font-medium text-slate-500 ml-0.5">{card.price_suffix}</span>
      </div>
      {card.footer_tagline && (
        <p className="text-xs text-slate-500 mt-auto mb-3 italic">{card.footer_tagline}</p>
      )}
      <a href={buildWhatsAppHref(card.whatsapp_number, card.whatsapp_prefilled_message)}
         target="_blank" rel="noopener noreferrer"
         data-testid={`college-card-${card.card_id}-wa-cta`}
         data-wa-number={card.whatsapp_number}
         className="text-sm font-bold w-full text-center px-4 py-3 rounded-xl text-white inline-flex items-center justify-center gap-1.5 min-h-[44px]"
         style={{ backgroundColor: showBadge ? card.border_color : ORANGE }}>
        <MessageCircle className="h-4 w-4"/> {card.whatsapp_button_text}
      </a>
    </div>
  );
}

export function CollegePricingPageView({ page, cards, variant = "desktop" }) {
  const visible = (cards || [])
                      .filter((c) => c.active !== false)
                      .slice()
                      .sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0));
  const wrapClass = variant === "mobile"
    ? "max-w-md mx-auto px-4"
    : "max-w-6xl mx-auto px-4 sm:px-6 lg:px-8";
  const gridClass = variant === "mobile"
    ? "grid grid-cols-1 gap-4"
    : "grid grid-cols-1 md:grid-cols-3 gap-5";
  return (
    <div className={`${wrapClass} py-10`} data-testid="college-pricing-view">
      <div className="text-center mb-8">
        <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900"
            data-testid="college-pricing-heading">
          {page.heading}
        </h1>
        {page.subheading && (
          <p className="mt-2 text-slate-600 max-w-xl mx-auto text-sm sm:text-base"
              data-testid="college-pricing-subheading">
            {page.subheading}
          </p>
        )}
        {page.highlight && (
          <p className="mt-2 text-[#FF6B00] font-semibold text-sm"
              data-testid="college-pricing-highlight">
            {page.highlight}
          </p>
        )}
      </div>
      <div className={`${gridClass} items-stretch pt-4`}>
        {visible.map((c) => (
          <PricingCard key={c.card_id} card={c}
                          badgeEnabled={!!page.most_popular_enabled}
                          badgeText={page.most_popular_badge_text || "MOST POPULAR"}/>
        ))}
      </div>
    </div>
  );
}
